// Copyright (c) 2026 Michael F. Kamprath
//
// animation-manager.js — Phase 5 tap animation orchestration.
//
// Implements the "See and Say" style tap animation from the original
// iOS app: when a child taps a button, the tapped button grows and
// translates toward the center of its page's grid while every other
// button on that page fades out and shrinks. The tapped button stays
// enlarged for the entire duration of the audio playback sequence
// (including the verbal description intro when that setting is on),
// then everything reverses back to rest when the audio ends.
//
// Timings (matched to the legacy Obj-C app — see
// AnimalSoundsViewController.m, setupAnimation):
//
//   Enter: 1.00 s ease-out, grow to scale 2.0, siblings shrink to
//          scale 0.75 and fade to opacity 0.
//   Exit:  0.75 s ease-out, everything back to rest.
//
// These durations live in :root as CSS custom properties
// (--hero-enter-duration / --hero-exit-duration) so the JS reads them
// via getComputedStyle and stays in sync with the stylesheet. Each
// phase sets its transition-duration inline on the affected elements
// for the duration of that phase, which is what lets the enter and
// exit halves have different durations without any CSS gymnastics.
//
// Design notes:
//
//   * The animation is driven by class toggles for state
//     (.is-hero, .is-dimmed) plus inline style writes for the things
//     that need per-tap values: the translate-to-center transform,
//     and the per-phase transition timing. Adding .is-hero to the
//     tapped button raises its z-index and keeps it painting above
//     siblings; adding .is-dimmed to the siblings fades them out.
//
//   * Playback orchestration uses the sound-manager's play() promise,
//     which resolves when the audio sequence finishes OR when a newer
//     play() supersedes it. We await that same promise here so the
//     animation "hold" phase is naturally bounded to the audio length
//     and is automatically released when the user taps another button.
//     A defensive HOLD_MAX_MS cap ensures the hero can never stay up
//     forever if the audio promise ever fails to settle for any reason.
//
//   * Rapid-tap handling follows the same interrupt-and-restart
//     pattern the sound manager uses: every call to runTapAnimation
//     bumps a monotonic animToken, any in-flight sequence checks its
//     token after each await boundary, and a fresh call first
//     snaps any previous sequence back to rest with transitions
//     disabled so the new hero can start cleanly.
//
//   * jsdom note: this module is written so the tests can run against
//     a pure-JS DOM. `transitionend` would never fire in jsdom, so
//     the exit wait uses a timer-based helper with a small safety
//     buffer over the CSS transition duration. Both real browsers
//     and jsdom reach the same final DOM state after the same wall
//     time, which keeps the module simple to assert against.

/**
 * @typedef {Object} StartAudioArgs
 * @property {Promise<void>} enterComplete  Resolves when the button
 *   has finished its grow animation and is at full scale. Consumers
 *   (typically sound-manager) use this as a gate to hold back the
 *   animal sound until the button stops moving, so the audible
 *   payload always lands on a still image. For short intro voices in
 *   other locales this means: intro plays during the grow, and the
 *   animal sound waits on the gate if the intro finishes before the
 *   grow does. For the verbal-descriptions-off case the sole item in
 *   the sequence is the animal, so it waits on the gate directly.
 *   The gate ALWAYS resolves (even on cancellation/destroy), backed
 *   by a setTimeout, so awaiters never hang.
 */

/**
 * @typedef {Object} AnimationManager
 * @property {(button: HTMLElement, startAudio: (args: StartAudioArgs) => Promise<void>) => Promise<void>} runTapAnimation
 * @property {() => void} cancelCurrent
 * @property {() => void} destroy
 */

// Default durations (ms) if the CSS custom properties aren't set. The
// production CSS does set them, but tests run in jsdom which returns
// empty strings for custom property lookups — these fallbacks are what
// the tests assert against.
const DEFAULT_ENTER_MS = 1000;
const DEFAULT_EXIT_MS = 750;
const EXIT_BUFFER_MS = 40; // safety margin over the exit transition
const DEFAULT_HERO_SCALE = 2.0;
const DEFAULT_EASE = 'ease-out';

// Safety cap for the hold phase. This is a defensive backstop only —
// in normal operation the audio promise resolves on its own when the
// last clip ends, and the cap is never hit. We size it generously so
// it never lops off the tail of a real clip:
//
//   * The longest sound in the catalog (truck) is 10.06 s. The longest
//     intro is ~2.6 s. Worst case, a tap with verbal descriptions on
//     plays for ~12.7 s of audio after the 1 s grow — call it ~14 s
//     wall clock. Earlier revs of this constant were 10_000 ms, which
//     was below that floor: the airplane (9.77 s sound + 2.22 s intro)
//     would visibly drop the hero before its sound finished playing.
//   * 60 s is far enough above any plausible future content that the
//     cap can only fire on a genuinely wedged audio promise — and
//     even then, the user can interrupt at any time by tapping
//     another button (or the same one) which bumps animToken and
//     bails out of the held sequence.
const HOLD_MAX_MS = 60000;

// Attribute name set on <html> while a hero is on screen. Stylesheets
// (notably grid.css) key off this to block pane swipes and disable
// other interactions during the zoom animation, since allowing the
// child to scroll-snap to a different category mid-zoom leaves the
// hero half-applied to a page they're no longer looking at.
const HERO_ACTIVE_ATTR = 'data-hero-active';

/**
 * Reflect "a hero is currently on screen" on document.documentElement
 * via a data attribute. Stylesheets watch this to lock down anything
 * that would interfere with the zoom: grid scroll-snap, button taps
 * on the off-screen pages, and so on.
 *
 * Guarded against missing document/documentElement so the module
 * stays safe to import in non-browser contexts (Node tooling, SSR
 * snapshots) — same defensive pattern as shouldReduceMotion below.
 */
function setHeroActiveAttr(active) {
  if (typeof document === 'undefined' || !document.documentElement) return;
  if (active) {
    document.documentElement.setAttribute(HERO_ACTIVE_ATTR, '');
  } else {
    document.documentElement.removeAttribute(HERO_ACTIVE_ATTR);
  }
}

/**
 * Build a new animation manager. Stateless from the caller's perspective
 * beyond the single "currently-animating" slot — there is never more
 * than one hero at a time.
 *
 * @param {{ now?: () => number }} [opts]
 * @returns {AnimationManager}
 */
export function createAnimationManager(opts = {}) {
  const _now = opts.now ?? (() => Date.now());
  void _now; // reserved for future analytics hooks; no runtime use yet

  /** Monotonic token bumped on every call. In-flight sequences whose
   *  token no longer matches bail out at the next await boundary. */
  let animToken = 0;

  /** @type {{ button: HTMLElement, siblings: HTMLElement[], img: HTMLElement } | null} */
  let currentState = null;

  let destroyed = false;

  /**
   * Run the full tap animation sequence: enter (grow + dim siblings),
   * hold (await audio, capped at HOLD_MAX_MS), exit (reverse). Resolves
   * when the sequence is either fully reversed or aborted by a newer
   * call / destroy.
   *
   * The manager OWNS the audio start. It is given a `startAudio`
   * function rather than a pre-created promise, and it passes a
   * single enterComplete gate promise in to that function. The caller
   * (typically sound-manager) is free to use the gate to hold back
   * individual clips in its playback sequence — in practice the
   * animal sound is gated on it so it only plays when the button has
   * finished growing. This gives us one clean mechanism that handles
   * all three cases:
   *
   *   * verbal on, long intro: intro plays during the grow, finishes
   *     after the grow completes, gate is already resolved, animal
   *     plays immediately on intro-end.
   *
   *   * verbal on, short intro: intro plays during the grow, finishes
   *     early, sound-manager blocks on the gate, animal fires the
   *     instant the button reaches max size — silent gap between
   *     intro-end and animal-start is intentional.
   *
   *   * verbal off: sequence is just [animal]; sound-manager blocks
   *     on the gate directly before playing the animal sound.
   *
   * The gate promise is backed by a setTimeout(enterMs), so it always
   * resolves eventually — even if the animation is cancelled or
   * destroyed. Sound-manager's own token-bump pattern is responsible
   * for bailing out of a stale play() call once the gate resolves.
   *
   * @param {HTMLElement} button  The `.grid__button` that was tapped.
   * @param {(args: StartAudioArgs) => Promise<void>} startAudio
   *   Called by the manager synchronously, inside the user-gesture
   *   stack, to kick off the audio sequence. Receives { enterComplete }
   *   and must return a promise that resolves when the sequence ends
   *   (or is superseded). Errors thrown synchronously or asynchronously
   *   are caught and logged — they never reject runTapAnimation itself.
   */
  async function runTapAnimation(button, startAudio) {
    if (destroyed) return;
    if (!(button instanceof HTMLElement)) return;
    if (typeof startAudio !== 'function') return;

    // Snap any prior animation back to rest instantly. cancelCurrent()
    // also bumps animToken, so any older in-flight sequence will drop
    // out at its next await boundary instead of trying to reverse a
    // button we're about to use as the new hero.
    cancelCurrent();
    const myToken = ++animToken;

    // --- Reduced-motion short path ------------------------------------------
    // WCAG 2.3.3 (Animation from Interactions): non-essential motion
    // triggered by interaction must be disableable. Our hero
    // grow/shrink isn't essential — the audio sequence is what matters
    // — so when the OS asks for reduced motion we skip the entire
    // hero pipeline and just play the sounds. The button's own
    // `:active` press-state (styled in CSS) still provides tactile
    // feedback, but nothing translates, scales, or fades. We re-query
    // matchMedia on every tap so that if the user toggles the OS
    // preference mid-session the very next tap honors the new state
    // without needing a reload or a change listener.
    //
    // iOS audio-start quirk — the gate MUST be setTimeout-backed, not
    // Promise.resolve(). Early revs of this branch used
    // `enterComplete: Promise.resolve()`, which meant sound-manager's
    // `await gate` resolved in a microtask that chained directly off
    // the tap handler's gesture frame. backend.play() →
    // source.start(0) then ran in that same microtask batch, and iOS
    // Safari silently refused to produce audio. The normal path
    // accidentally sidesteps this by backing enterComplete with
    // `setTimeout(resolve, enterMs)` (where enterMs is the 1 s hero
    // grow), so the await gate resumes inside a fresh timer task and
    // source.start() plays normally. We need the same task boundary
    // here; using `setTimeout(resolve, 0)` gives us "a fresh task,
    // as soon as possible" without making the user wait.
    if (shouldReduceMotion()) {
      // Gate resolves at the next task boundary (not microtask) so
      // sound-manager's `await gate` resumes inside a setTimeout
      // callback, matching the normal path's timing model.
      const enterComplete = new Promise((resolve) => {
        setTimeout(resolve, 0);
      });
      const audioPromise = invokeStartAudio(startAudio, { enterComplete });
      // Still honor the HOLD_MAX_MS safety cap. The cap exists to
      // protect against a wedged sound-manager promise, not to
      // protect against animation state, so it applies equally to
      // the reduced-motion path.
      let rmSafetyTimer;
      try {
        await Promise.race([
          audioPromise,
          new Promise((resolve) => {
            rmSafetyTimer = setTimeout(resolve, HOLD_MAX_MS);
          }),
        ]);
      } catch (err) {
        console.warn('[animation-manager] audio promise rejected:', err);
      } finally {
        if (rmSafetyTimer) clearTimeout(rmSafetyTimer);
      }
      return;
    }

    const img = button.querySelector('.grid__button-img');
    if (!(img instanceof HTMLElement)) return;

    // The siblings are exactly the other buttons within the same
    // `.grid__buttons` container. Scoping to the parent means buttons
    // on other pages (which are off-screen via scroll-snap) are never
    // touched — they stay tappable if the user swipes away mid-animation.
    const buttonGrid = button.parentElement;
    if (!buttonGrid || !buttonGrid.classList.contains('grid__buttons')) return;

    const siblings = /** @type {HTMLElement[]} */ (
      Array.from(buttonGrid.querySelectorAll('.grid__button')).filter(
        (el) => el !== button,
      )
    );

    // Compute the pixel offset from the button's current center to the
    // center of its grid. Corner buttons get a large translate, the
    // already-centered button (if any) gets near-zero. The result is
    // applied as an inline transform so the CSS class itself doesn't
    // have to know about the cell geometry.
    const gridRect = buttonGrid.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const gridCenterX = gridRect.left + gridRect.width / 2;
    const gridCenterY = gridRect.top + gridRect.height / 2;
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const buttonCenterY = buttonRect.top + buttonRect.height / 2;
    const dx = gridCenterX - buttonCenterX;
    const dy = gridCenterY - buttonCenterY;

    // Read the animation tokens from CSS so one place (main.css) owns
    // them. jsdom returns an empty string for custom-prop lookups so
    // the defaults apply in tests.
    const heroScale = readCssNumber(button, '--hero-scale', DEFAULT_HERO_SCALE);
    const enterMs = readCssDuration(
      button,
      '--hero-enter-duration',
      DEFAULT_ENTER_MS,
    );
    const exitMs = readCssDuration(
      button,
      '--hero-exit-duration',
      DEFAULT_EXIT_MS,
    );
    // --hero-ease is declared as a plain keyword ('ease-out') in CSS
    // which is all we need — just pass it through unmodified, falling
    // back to ease-out when unset.
    const ease = readCssString(button, '--hero-ease', DEFAULT_EASE);

    // --- Enter ---------------------------------------------------------------
    // Apply state classes first, then set per-phase inline transitions
    // so both the tapped button's img and every sibling's img+opacity
    // transition at the enter duration. Finally force a reflow and set
    // the inline transform target to kick the transition off in the
    // same frame.
    button.classList.add('is-hero');
    // Mark the document as "hero active" so global stylesheets can
    // freeze interactions that would interfere with the zoom — most
    // importantly, lock the grid's horizontal scroll-snap so the
    // child can't swipe between categories mid-animation and end up
    // looking at a different page than the one their hero lives on.
    setHeroActiveAttr(true);
    siblings.forEach((s) => s.classList.add('is-dimmed'));

    const transformEnter = `transform ${enterMs}ms ${ease}`;
    const opacityEnter = `opacity ${enterMs}ms ${ease}`;
    img.style.transition = transformEnter;
    for (const s of siblings) {
      s.style.transition = opacityEnter;
      const simg = s.querySelector('.grid__button-img');
      if (simg instanceof HTMLElement) {
        simg.style.transition = transformEnter;
      }
    }

    // Force a synchronous reflow so the style-recalc pipeline commits
    // the above before the transform target change below. Without this,
    // the browser can coalesce both writes into a single frame and the
    // transition skips its "from" state.
    void button.offsetHeight;

    img.style.transform = `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) scale(${heroScale})`;

    currentState = { button, siblings, img };

    // --- Audio kickoff -------------------------------------------------------
    // Invoke startAudio synchronously and pass it an enterComplete
    // gate promise that resolves when the grow animation finishes.
    // The sound manager uses the gate to hold back the animal sound
    // until the button is fully still — see the long comment above
    // runTapAnimation for the three cases this handles.
    //
    // The gate is backed by its own setTimeout so it always resolves
    // regardless of whether this runTapAnimation is cancelled mid-enter.
    // Stale play() awaiters wake up on gate resolve and then bail via
    // their own token check inside sound-manager.
    //
    // startAudio runs synchronously in the tap handler's gesture frame
    // so iOS audio can begin without triggering a fresh-unlock
    // requirement. The audio-start path was already unlocked at splash
    // dismissal, but keeping startAudio gesture-sync is still the
    // safest contract.
    const enterComplete = new Promise((resolve) => {
      setTimeout(resolve, enterMs);
    });
    const audioPromise = invokeStartAudio(startAudio, { enterComplete });

    // --- Hold ----------------------------------------------------------------
    // Wait for the audio sequence to complete, OR until the safety cap
    // elapses. The sound manager's play() promise is supposed to resolve
    // on audio end or on interrupt, but we don't trust it completely —
    // a suspended audio context, a decode failure that slips past the
    // error path, or any future regression could leave it hanging, and
    // the hero must never stay stuck forever.
    let safetyTimer;
    try {
      await Promise.race([
        audioPromise,
        new Promise((resolve) => {
          safetyTimer = setTimeout(() => {
            console.warn(
              '[animation-manager] audio promise did not settle within HOLD_MAX_MS; releasing hero',
            );
            resolve();
          }, HOLD_MAX_MS);
        }),
      ]);
    } catch (err) {
      console.warn('[animation-manager] audio promise rejected:', err);
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
    }
    if (myToken !== animToken || destroyed) return;

    // --- Exit ----------------------------------------------------------------
    // Swap inline transitions to the exit duration, then clear the
    // inline transform and drop .is-dimmed. .is-hero stays on for
    // EXIT_BUFFER_MS past the transition so the button keeps its
    // elevated z-index for the entire return animation.
    const transformExit = `transform ${exitMs}ms ${ease}`;
    const opacityExit = `opacity ${exitMs}ms ${ease}`;
    img.style.transition = transformExit;
    for (const s of siblings) {
      s.style.transition = opacityExit;
      const simg = s.querySelector('.grid__button-img');
      if (simg instanceof HTMLElement) {
        simg.style.transition = transformExit;
      }
    }
    void button.offsetHeight;

    img.style.transform = '';
    siblings.forEach((s) => s.classList.remove('is-dimmed'));

    await wait(exitMs + EXIT_BUFFER_MS);
    if (myToken !== animToken || destroyed) return;

    button.classList.remove('is-hero');
    // Hero animation is fully complete — release the document-level
    // lock so pane swipes and other interactions return to normal.
    // The mirror call lives in cancelCurrent() to cover the rapid-tap
    // / destroy paths.
    setHeroActiveAttr(false);

    // Clear the per-phase inline transitions so the next tap starts
    // from a clean state and the CSS base rules (short press-down
    // transition) take over again.
    img.style.transition = '';
    for (const s of siblings) {
      s.style.transition = '';
      const simg = s.querySelector('.grid__button-img');
      if (simg instanceof HTMLElement) {
        simg.style.transition = '';
      }
    }

    currentState = null;
  }

  /**
   * Abort whatever hero sequence is currently in-flight and snap the
   * affected elements back to rest instantly. Bumps animToken so any
   * suspended sequence exits at its next token check. Safe to call
   * when there is no active animation.
   */
  function cancelCurrent() {
    animToken++;
    if (!currentState) return;

    const { button, siblings, img } = currentState;

    // Temporarily disable transitions on the hero and its siblings so
    // the cleanup below snaps to rest instead of playing a reverse
    // animation. Without this, the about-to-be-replaced hero would
    // briefly scale back down while the new hero is scaling up.
    button.classList.add('anim-no-transition');
    siblings.forEach((s) => s.classList.add('anim-no-transition'));

    img.style.transform = '';
    img.style.transition = '';
    button.classList.remove('is-hero');
    // Release the document-level "hero active" lock. If a fresh
    // runTapAnimation is about to start (the rapid-tap case),
    // it will re-set the attribute the moment its own enter phase
    // begins, so any user-visible flicker between clear and re-set
    // is shorter than a single frame.
    setHeroActiveAttr(false);
    siblings.forEach((s) => {
      s.classList.remove('is-dimmed');
      s.style.transition = '';
      const simg = s.querySelector('.grid__button-img');
      if (simg instanceof HTMLElement) simg.style.transition = '';
    });

    // Force a reflow so the "no transform, no class" state is committed
    // to the rendering pipeline *before* we remove the no-transition
    // class and the next runTapAnimation re-enables transitions.
    void button.offsetHeight;

    button.classList.remove('anim-no-transition');
    siblings.forEach((s) => s.classList.remove('anim-no-transition'));

    currentState = null;
  }

  function destroy() {
    destroyed = true;
    cancelCurrent();
  }

  return { runTapAnimation, cancelCurrent, destroy };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read a numeric CSS custom property off an element's computed style.
 * Falls back to the supplied default when the property is missing,
 * non-numeric, or when getComputedStyle is unavailable (jsdom).
 */
function readCssNumber(element, prop, fallback) {
  try {
    const cs =
      typeof getComputedStyle === 'function' ? getComputedStyle(element) : null;
    const raw = cs ? cs.getPropertyValue(prop) : '';
    const num = Number.parseFloat(raw);
    return Number.isFinite(num) ? num : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Read a CSS duration custom property (e.g. "1000ms" or "0.75s") and
 * return the value in milliseconds. Falls back to the supplied default
 * for unset / invalid / unavailable lookups.
 */
function readCssDuration(element, prop, fallbackMs) {
  try {
    const cs =
      typeof getComputedStyle === 'function' ? getComputedStyle(element) : null;
    const raw = cs ? cs.getPropertyValue(prop).trim() : '';
    if (!raw) return fallbackMs;
    if (raw.endsWith('ms')) {
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) ? n : fallbackMs;
    }
    if (raw.endsWith('s')) {
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) ? n * 1000 : fallbackMs;
    }
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n : fallbackMs;
  } catch {
    return fallbackMs;
  }
}

/**
 * Read a string CSS custom property (for easing keywords like
 * "ease-out" or "cubic-bezier(...)"). Falls back when the property is
 * unset or getComputedStyle is unavailable.
 */
function readCssString(element, prop, fallback) {
  try {
    const cs =
      typeof getComputedStyle === 'function' ? getComputedStyle(element) : null;
    const raw = cs ? cs.getPropertyValue(prop).trim() : '';
    return raw || fallback;
  } catch {
    return fallback;
  }
}

/** Resolve after `ms` milliseconds. Used as a transition-duration wait
 *  that works in both real browsers and jsdom. */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Query the OS-level "reduced motion" preference. Wrapped in a helper
 * because matchMedia is missing in some environments (jsdom without
 * the matchMedia polyfill, stripped service-worker contexts) and we
 * want a conservative "false" fallback rather than an exception.
 * The return value is used by runTapAnimation to branch onto a
 * motion-free short path.
 */
function shouldReduceMotion() {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches === true
    );
  } catch {
    return false;
  }
}

/**
 * Invoke a caller-supplied startAudio function and return a promise
 * that resolves when the audio sequence finishes. Defensive: if the
 * function throws synchronously, returns a non-thenable, or rejects,
 * the error is logged and a resolved promise is returned so the
 * animation still runs its hold + exit phases normally. The caller
 * should never see its own startAudio failure cascade into a stuck
 * hero state.
 *
 * @param {(args: StartAudioArgs) => Promise<void>} startAudio
 * @param {StartAudioArgs} args  Forwarded verbatim to startAudio.
 */
function invokeStartAudio(startAudio, args) {
  try {
    const result = startAudio(args);
    if (result && typeof result.then === 'function') {
      return Promise.resolve(result).catch((err) => {
        console.warn('[animation-manager] startAudio rejected:', err);
      });
    }
    return Promise.resolve();
  } catch (err) {
    console.warn('[animation-manager] startAudio threw:', err);
    return Promise.resolve();
  }
}
